// services/patientApi.ts
import { 
  PatientFeedback, 
  FeedbackSubmissionData, 
  SatisfactionRating,
  SubmissionResponse 
} from '../store/patientFeedbackSlice';
import { 
  PatientSession, 
  SessionCreateData, 
  SessionPreferences 
} from '../store/patientSessionSlice';
import { API_BASE_URL } from './api';

// Base API configuration for patient endpoints
const PATIENT_API_BASE = `${API_BASE_URL}/patient`;

// Response type for paginated results
interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// Error handling utility
class PatientAPIError extends Error {
  constructor(
    message: string,
    public status?: number,
    public details?: any
  ) {
    super(message);
    this.name = 'PatientAPIError';
  }
}

// Utility to handle API responses
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new PatientAPIError(
      errorData.message || `HTTP ${response.status}: ${response.statusText}`,
      response.status,
      errorData
    );
  }
  
  return await response.json();
}

// Patient Session API
export const patientSessionApi = {
  // Create a new anonymous patient session
  async createSession(data: SessionCreateData = {}): Promise<{
    session_id: string;
    expires_at: string;
    message: string;
  }> {
    const response = await fetch(`${PATIENT_API_BASE}/sessions/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        language: data.language || 'en',
        allow_analytics: data.allow_analytics || false,
        allow_follow_up: data.allow_follow_up || false,
        contact_method: data.contact_method || 'none',
      }),
    });
    
    return handleResponse(response);
  },

  // Validate current session
  async validateSession(): Promise<PatientSession> {
    const response = await fetch(`${PATIENT_API_BASE}/sessions/validate/`, {
      credentials: 'include',
    });
    
    return handleResponse(response);
  },

  // Refresh session expiry
  async refreshSession(): Promise<{ expires_at: string; message: string }> {
    const response = await fetch(`${PATIENT_API_BASE}/sessions/refresh/`, {
      method: 'POST',
      credentials: 'include',
    });
    
    return handleResponse(response);
  },

  // Update session preferences
  async updatePreferences(sessionId: string, preferences: Partial<SessionPreferences>): Promise<PatientSession> {
    const response = await fetch(`${PATIENT_API_BASE}/sessions/${sessionId}/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(preferences),
    });
    
    return handleResponse(response);
  },

  // Get session details
  async getSession(sessionId: string): Promise<PatientSession> {
    const response = await fetch(`${PATIENT_API_BASE}/sessions/${sessionId}/`, {
      credentials: 'include',
    });
    
    return handleResponse(response);
  },
};

// Patient Feedback API
export const patientFeedbackApi = {
  // Submit new feedback
  async submitFeedback(data: FeedbackSubmissionData): Promise<SubmissionResponse> {
    const response = await fetch(`${PATIENT_API_BASE}/feedback/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    
    return handleResponse(response);
  },

  // Get feedback list for current session
  async getFeedbackList(): Promise<PatientFeedback[]> {
    const response = await fetch(`${PATIENT_API_BASE}/feedback/`, {
      credentials: 'include',
    });
    
    const data: PaginatedResponse<PatientFeedback> = await handleResponse(response);
    return data.results;
  },

  // Get specific feedback details
  async getFeedback(feedbackId: number): Promise<PatientFeedback> {
    const response = await fetch(`${PATIENT_API_BASE}/feedback/${feedbackId}/`, {
      credentials: 'include',
    });
    
    return handleResponse(response);
  },

  // Track feedback status
  async trackFeedback(feedbackId: number): Promise<PatientFeedback> {
    const response = await fetch(`${PATIENT_API_BASE}/feedback/${feedbackId}/status/`, {
      credentials: 'include',
    });
    
    return handleResponse(response);
  },

  // Submit satisfaction rating
  async rateSatisfaction(feedbackId: number, rating: SatisfactionRating): Promise<{
    message: string;
    rating: number;
  }> {
    const response = await fetch(`${PATIENT_API_BASE}/feedback/${feedbackId}/rate/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(rating),
    });
    
    return handleResponse(response);
  },

  // Upload file attachment
  async uploadFile(file: File, feedbackId?: number): Promise<{
    feedback_id: number;
    tracking_number: string;
    file_id: number;
    message: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    
    if (feedbackId) {
      formData.append('feedback_id', feedbackId.toString());
    }
    
    const response = await fetch(`${PATIENT_API_BASE}/feedback/upload/`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    
    return handleResponse(response);
  },

  // Upload multiple files
  async uploadFiles(files: File[], feedbackId?: number): Promise<Array<{
    feedback_id: number;
    tracking_number: string;
    file_id: number;
    message: string;
  }>> {
    const uploadPromises = files.map(file => this.uploadFile(file, feedbackId));
    return Promise.all(uploadPromises);
  },
};

// Feedback Categories API
export const feedbackCategoriesApi = {
  // Get available feedback categories
  async getCategories(): Promise<{
    categories: Array<{
      id: string;
      name: string;
      description: string;
      icon?: string;
      requires_contact?: boolean;
      suggested_fields?: string[];
    }>;
  }> {
    const response = await fetch(`${PATIENT_API_BASE}/categories/`, {
      credentials: 'include',
    });
    
    return handleResponse(response);
  },

  // Get category-specific form fields
  async getCategoryFields(categoryId: string): Promise<{
    fields: Array<{
      name: string;
      type: 'text' | 'textarea' | 'select' | 'checkbox' | 'file';
      label: string;
      required: boolean;
      options?: string[];
      placeholder?: string;
      help_text?: string;
    }>;
  }> {
    const response = await fetch(`${PATIENT_API_BASE}/categories/${categoryId}/fields/`, {
      credentials: 'include',
    });
    
    return handleResponse(response);
  },
};

// Analytics API (if allowed by user preferences)
export const patientAnalyticsApi = {
  // Get patient portal usage statistics
  async getPortalStats(): Promise<{
    total_sessions: number;
    total_feedback: number;
    avg_response_time_hours: number;
    satisfaction_average: number;
    top_categories: Array<{ category: string; count: number }>;
  }> {
    const response = await fetch(`${PATIENT_API_BASE}/analytics/portal/`, {
      credentials: 'include',
    });
    
    return handleResponse(response);
  },

  // Get feedback resolution trends
  async getResolutionTrends(): Promise<{
    resolution_times: Array<{
      date: string;
      avg_hours: number;
      total_resolved: number;
    }>;
    satisfaction_trends: Array<{
      date: string;
      avg_rating: number;
      total_ratings: number;
    }>;
  }> {
    const response = await fetch(`${PATIENT_API_BASE}/analytics/trends/`, {
      credentials: 'include',
    });
    
    return handleResponse(response);
  },
};

// Real-time updates via Server-Sent Events
export class PatientUpdatesStream {
  private eventSource: EventSource | null = null;
  private listeners: { [event: string]: ((data: any) => void)[] } = {};

  connect(): void {
    if (this.eventSource) {
      return; // Already connected
    }

    this.eventSource = new EventSource(`${PATIENT_API_BASE}/updates/stream/`, {
      withCredentials: true,
    });

    this.eventSource.onopen = () => {
      // Patient updates stream connected
    };

    this.eventSource.onerror = (error) => {
      console.warn('Patient updates stream error (non-critical):', error);
      this.disconnect();
      
      // Don't attempt to reconnect - just log for debugging
      // The patient feedback page will work fine without real-time updates
      console.info('Real-time updates disabled due to EventSource error');
    };

    // Handle different types of updates
    this.eventSource.addEventListener('feedback_status', (event) => {
      const data = JSON.parse(event.data);
      this.emit('feedback_status', data);
    });

    this.eventSource.addEventListener('staff_response', (event) => {
      const data = JSON.parse(event.data);
      this.emit('staff_response', data);
    });

    this.eventSource.addEventListener('session_update', (event) => {
      const data = JSON.parse(event.data);
      this.emit('session_update', data);
    });
  }

  disconnect(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  on(event: string, callback: (data: any) => void): void {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  }

  off(event: string, callback: (data: any) => void): void {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    }
  }

  private emit(event: string, data: any): void {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => callback(data));
    }
  }
}

// Export singleton instance for updates stream
export const patientUpdatesStream = new PatientUpdatesStream();

// Utility functions
export const patientApiUtils = {
  // Check if error is due to session expiry
  isSessionExpired(error: any): boolean {
    return error instanceof PatientAPIError && error.status === 401;
  },

  // Format file size for display
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  },

  // Validate file type and size
  validateFile(file: File): { valid: boolean; error?: string } {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'image/jpeg',
      'image/png', 
      'image/gif',
      'application/pdf',
      'text/plain',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    if (file.size > maxSize) {
      return { valid: false, error: 'File size exceeds 10MB limit' };
    }

    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: 'File type not supported' };
    }

    return { valid: true };
  },

  // Generate tracking display format
  formatTrackingNumber(trackingNumber: string): string {
    // Format like: FB-2024-001234
    return trackingNumber.replace(/^([A-Z]{2})(\d{4})(\d{6})$/, '$1-$2-$3');
  },

  // Calculate time since submission
  getTimeSince(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    
    const minutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (minutes > 0) {
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  },
};

// Export all APIs
export default {
  session: patientSessionApi,
  feedback: patientFeedbackApi,
  categories: feedbackCategoriesApi,
  analytics: patientAnalyticsApi,
  updates: patientUpdatesStream,
  utils: patientApiUtils,
};